import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { userHasAccess } from "../../services/RoleBasedAccess";
import { Account, Asset } from "../../state/types";
import { useSession } from "../../context/SessionContext";
import { useAssetApi } from "../../api/useAssetApi";
import { stripUnitSuffix } from "../../utils/systemCode";
import { buildAssetLookupCandidates, findMatchingAsset } from "./assetLookup";

export interface ImageCategory {
  key: string;
  label: string;
  images: string[];
  visible?: boolean;
}

const IMAGE_TYPE_MAP: Record<string, string> = {
  'imageExterior': 'exterior_raw',
  'imageManual': 'manual_raw',
  'imagePano': 'pano_raw',
  'imageFeatured': 'featured',
  'imageMask': 'masking',
  'imageCrop': 'cropping',
  'imageUndercarriage': 'undercarriage_raw'
};

const S3_FOLDER_MAP: Record<string, string> = {
  'imageExterior': 'img_ext',
  'imagePano': 'img_pano',
  'imageManual': 'img_man',
  'imageMask': 'img_mask',
  'imageCrop': 'img_crop',
  'imageFeatured': 'img_featured',
  'imageBackgroundReplaced': 'img_bgr',
  'imageUndercarriage': 'img_uc',
};

function generateImageCategories(asset: Asset | null): ImageCategory[] {
  if (!asset) return []

  const hasBgrPano = !!asset.imageBgrPano?.length;
  const panoImages = hasBgrPano ? asset.imageBgrPano : asset.imagePano;
  const panoLabel = hasBgrPano ? "Panoramic (BGR)" : "Panoramic";

  const hasDewarp = !!asset.imageUndercarriageDewarp?.length;
  const ucFallback = asset.imageUndercarriage || [];

  return [
    { key: "exterior", label: "Exterior", images: asset.imageExterior || [] },
    { key: "crop", label: "Cropped", images: asset.imageCrop || [] },
    { key: "featured", label: "Featured", images: asset.imageFeatured || [] },
    { key: "bgr", label: "Background Replaced", images: asset.imageBgr || [] },
    { key: "manual", label: "Manual", images: asset.imageManual || [] },
    { key: "pano", label: panoLabel, images: panoImages || [] },
    {
      key: "uc",
      label: hasDewarp ? "Undercarriage" : "Raw Undercarriage",
      images: hasDewarp ? asset.imageUndercarriageDewarp : ucFallback,
    },
  ];
}

export const useAssetState = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { assetId: assetInstanceId, systemCode: routeSystemCode } = useParams<{ assetId: string; systemCode: string }>();
  const systemCode = stripUnitSuffix(routeSystemCode);
  const { user, setIsLoading } = useSession();
  const {
    getAssetsApi,
    getAssetErrors,
    getUploadUrlApi,
    uploadToS3Api,
    updateImageOrderApi
  } = useAssetApi();
  const userLoaded = !!user && !!user.role;

  const [asset, setAsset] = useState<Asset | null>(null);
  const [errors, setErrors] = useState<any[]>([]);
  const [visibleTypes, setVisibleTypes] = useState<{ [key: string]: boolean }>({});
  const [isEditAssetIdModalOpen, setIsEditAssetIdModalOpen] = useState(false);
  const [isEditAssetModalOpen, setIsEditAssetModalOpen] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const setInitialVisibleTypes = useCallback((currentAsset: Asset) => {
    setVisibleTypes({
      exterior: !!currentAsset.imageExterior?.length,
      crop: !!currentAsset.imageCrop?.length,
      featured: !!currentAsset.imageFeatured?.length,
      manual: !!currentAsset.imageManual?.length,
      pano: !!currentAsset.imageBgrPano?.length || !!currentAsset.imagePano?.length,
      uc: !!currentAsset.imageUndercarriageDewarp?.length || !!currentAsset.imageUndercarriage?.length,
      bgr: !!currentAsset.imageBgr?.length,
    });
  }, []);

  // Data authorization and fetching
  useEffect(() => {
    if (!userLoaded || !user) return;

    if (!routeSystemCode || !assetInstanceId) {
      setIsLoading(false);
      navigate("/");
      return;
    }

    setIsLoading(true);

    const baseSystemCode = stripUnitSuffix(routeSystemCode);
    const accessibleCodes = new Set((user.system_access || []).map((code: string) => stripUnitSuffix(code)));
    const isInternalUser = userHasAccess(user.role, ["Internal"]);
    if (!isInternalUser && !accessibleCodes.has(baseSystemCode)) {
      setIsLoading(false);
      navigate("/");
      return;
    }

    const locationAsset = (location.state as { asset?: Asset } | null)?.asset;
    const locationAssetInstanceId = locationAsset?.assetInstanceId || locationAsset?.assetId;
    const locationSystemCode = stripUnitSuffix(locationAsset?.systemCode);
    const canUseLocationAsset = !!locationAsset
      && locationSystemCode === systemCode
      && locationAssetInstanceId === assetInstanceId;
    const shouldUseCachedAsset = canUseLocationAsset && refetchTrigger === 0;

    if (shouldUseCachedAsset && locationAsset) {
      setAsset(locationAsset);
      setErrors([]);
      setInitialVisibleTypes(locationAsset);
      setIsLoading(false);
      return;
    }

    const getAssetData = async () => {
      try {
        let fetchedAsset: Asset | undefined;
        const lookupCandidates = buildAssetLookupCandidates(assetInstanceId);

        for (const assetIdCandidate of lookupCandidates) {
          const { assets } = await getAssetsApi({
            page: 1,
            per_page: 10,
            system_code: baseSystemCode,
            asset_id: assetIdCandidate,
            archived: true
          });

          fetchedAsset = findMatchingAsset(assets, assetInstanceId) || assets[0];
          if (fetchedAsset) break;
        }

        setAsset(fetchedAsset || null);

        const errorAssetId = fetchedAsset?.assetId || assetInstanceId.split("-")[0];
        const errorResponse = await getAssetErrors(baseSystemCode, errorAssetId);
        setErrors(errorResponse.errors || []);

        if (fetchedAsset) {
          setInitialVisibleTypes(fetchedAsset);
        }
      } catch (error) {
        console.error("Error fetching asset data:", error);
        setAsset(null);
      } finally {
        setIsLoading(false);
      }
    };

    getAssetData();
  }, [user, userLoaded, routeSystemCode, systemCode, assetInstanceId, navigate, refetchTrigger, location.state, getAssetsApi, getAssetErrors, setIsLoading, setInitialVisibleTypes]);

  // --- Document Title Effect (no change) ---
  useEffect(() => {
    const originalTitle = document.title;
    document.title = asset?.assetId || "Black Widow UMS";
    return () => { document.title = originalTitle; };
  }, [asset?.assetId]);

  const imageCategories = useMemo(() => generateImageCategories(asset), [asset]);

  const processMediaUpdates = useCallback(async (
    asset: Asset,
    uploadedFiles: any[],
    orderedImageTypes: Record<string, any[]>
  ) => {
    const sk = asset.pk.split('-')[1] || "";

    // 1. Handle Uploads
    const uploadResults = await Promise.all(uploadedFiles.map(async (item, idx) => {
      try {
        const target = IMAGE_TYPE_MAP[item.mediaGroup] || 'exterior_raw';
        const fileName = `${asset.assetIdentifier}-${String(idx + 1).padStart(2, '0')}.jpg`;

        const url = await getUploadUrlApi({
          clientName: asset.clientIdentifier || '',
          pk: asset.pk,
          fileName,
          target,
          contentType: item.file.type
        });

        await uploadToS3Api(url, item.file);

        return { success: true, fileName, imageGroup: item.mediaGroup };
      } catch (err) {
        console.error("Upload failed", err);
        return { success: false };
      }
    }));

    const successfulUploads = uploadResults.filter(r => r.success);

    // 2. Build updated arrays for DynamoDB
    const finalImageArrays: Record<string, any[]> = {};

    Object.entries(orderedImageTypes).forEach(([type, array]) => {
      finalImageArrays[type] = array.map(img => {
        if (img.isNew) {
          const upload = successfulUploads.shift();
          if (!upload) return null;

          const strippedPk = asset.pk.replace("asset#", "");
          const folder = S3_FOLDER_MAP[upload.imageGroup || ''] || 'exterior_raw';
          const s3Key = `${asset.clientIdentifier}/${strippedPk}/${folder}/${upload.fileName}`;

          return {
            imageTag: img.imageTag || 'New Image',
            imageUrl: `https://${asset.s3bucket}.s3.amazonaws.com/${s3Key}`,
            imageUrlCf: `https://${asset.cloudFrontUrl}/${s3Key}`,
            imageUrlS3: `s3://${asset.s3bucket}/${s3Key}`,
          };
        }
        return img;
      }).filter(Boolean);
    });

    // 3. Send updates to DynamoDB for changed arrays
    const updatePromises = Object.entries(finalImageArrays).map(([type, newArray]) => {
      const original = (asset as any)[type] || [];
      if (JSON.stringify(original) !== JSON.stringify(newArray)) {
        return updateImageOrderApi({ pk: asset.pk, sk, imageType: type, imageArray: newArray });
      }
      return null;
    });

    await Promise.all(updatePromises.filter(Boolean));
    refetchAsset(); // Refresh UI
  }, [getUploadUrlApi, uploadToS3Api, updateImageOrderApi]);


  const updateAssetMedia = useCallback(async (state: any) => {
    if (!asset) return;

    setIsLoading(true);
    try {
      const { asset: updatedAssetState, uploadedFiles } = state;
      const modifiedImageArrays: Record<string, any[]> = {};

      Object.keys(updatedAssetState).forEach(key => {
        if (key.startsWith('image') && Array.isArray(updatedAssetState[key])) {
          if (JSON.stringify(updatedAssetState[key]) !== JSON.stringify((asset as any)[key])) {
            modifiedImageArrays[key] = updatedAssetState[key];
          }
        }
      });

      await processMediaUpdates(asset, uploadedFiles, modifiedImageArrays);
    } catch (error) {
      console.error("Failed to update asset:", error);
    } finally {
      setIsLoading(false);
    }
  }, [asset, processMediaUpdates]);

  const refetchAsset = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  return {
    asset,
    assetId: assetInstanceId, // backward-compatible alias
    assetInstanceId,
    systemCode,
    errors,
    visibleTypes,
    setVisibleTypes,
    imageCategories,
    updateAssetMedia,
    refetchAsset,

    isEditAssetIdModalOpen,
    openEditAssetIdModal: () => setIsEditAssetIdModalOpen(true),
    closeEditAssetIdModal: () => setIsEditAssetIdModalOpen(false),

    isEditAssetModalOpen,
    openEditAssetModal: () => setIsEditAssetModalOpen(true),
    closeEditAssetModal: () => setIsEditAssetModalOpen(false),
  };
};
